import { useState, useEffect } from 'react'
import { Shield, Bell, Save, Lock } from 'lucide-react'
import { interviewerApi } from '../../lib/api/interviewer'
import apiClient from '../../lib/api/client'

// DiceBear style → avatar seed style mapping
const AVATAR_STYLES: { key: string; label: string; dicebear: string }[] = [
  { key: 'abstract',  label: 'Abstract',  dicebear: 'shapes' },
  { key: 'geometric', label: 'Geometric', dicebear: 'identicon' },
  { key: 'nature',    label: 'Nature',    dicebear: 'bottts' },
  { key: 'minimal',   label: 'Minimal',   dicebear: 'initials' },
]

function styleFromUrl(url: string | null): string {
  if (!url) return 'abstract'
  for (const s of AVATAR_STYLES) {
    if (url.includes(s.dicebear)) return s.key
  }
  return 'abstract'
}

function avatarUrlForStyle(style: string, seed: string): string {
  const s = AVATAR_STYLES.find((a) => a.key === style) ?? AVATAR_STYLES[0]
  return `https://api.dicebear.com/7.x/${s.dicebear}/svg?seed=${encodeURIComponent(seed)}`
}

interface Profile {
  displayName: string
  bio: string
  specializations: string
  years_experience: string
  avatarStyle: string
  avatarSeed: string
}

export default function InterviewerSettings() {
  const [profile, setProfile] = useState<Profile>({
    displayName: '',
    bio: '',
    specializations: '',
    years_experience: '',
    avatarStyle: 'abstract',
    avatarSeed: 'interviewer',
  })
  const [notifications, setNotifications] = useState({
    sessionReminders: true,
    evaluationReminders: true,
    newClients: true,
  })
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  })
  const [passwordError, setPasswordError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState('')
  const [submittingPassword, setSubmittingPassword] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saveMessage, setSaveMessage] = useState('')

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const data = await interviewerApi.getProfile()
        const alias = data.alias || ''
        setProfile({
          displayName: alias,
          bio: data.bio || '',
          specializations: data.specializations || '',
          years_experience: data.years_experience?.toString() || '',
          avatarStyle: styleFromUrl(data.avatar_url),
          avatarSeed: alias || 'interviewer',
        })
      } catch (err) {
        console.error('Failed to fetch profile:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchProfile()
  }, [])

  const handleSaveProfile = async () => {
    setSaving(true)
    try {
      const seed = profile.displayName.trim() || profile.avatarSeed
      await interviewerApi.updateProfile({
        alias: profile.displayName.trim() || undefined,
        bio: profile.bio,
        specializations: profile.specializations,
        years_experience: profile.years_experience
          ? parseInt(profile.years_experience)
          : null,
        avatar_url: avatarUrlForStyle(profile.avatarStyle, seed),
      })
      setSaveMessage('Profile updated successfully!')
      setTimeout(() => setSaveMessage(''), 3000)
    } catch (err) {
      console.error('Failed to save profile:', err)
      setSaveMessage('Failed to save profile.')
    } finally {
      setSaving(false)
    }
  }

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value })
    setPasswordError('')
    setPasswordSuccess('')
  }

  const handleUpdatePassword = async () => {
    setPasswordError('')
    setPasswordSuccess('')
    if (
      !passwordData.currentPassword ||
      !passwordData.newPassword ||
      !passwordData.confirmPassword
    ) {
      setPasswordError('Please fill in all password fields')
      return
    }
    if (passwordData.newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters')
      return
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match')
      return
    }
    setSubmittingPassword(true)
    try {
      await apiClient.put('/client/password', {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword,
        new_password_confirmation: passwordData.confirmPassword,
      })
      setPasswordSuccess('Password updated successfully')
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' })
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string; detail?: string } } })
          ?.response?.data?.message ||
        (err as { response?: { data?: { detail?: string } } })
          ?.response?.data?.detail ||
        'Failed to update password. Check your current password.'
      setPasswordError(msg)
    } finally {
      setSubmittingPassword(false)
    }
  }

  if (loading) {
    return <div className="loading-screen">Loading settings...</div>
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Settings</h1>
        <p className="page-subtitle">Manage your profile and preferences</p>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: 24,
          maxWidth: 800,
        }}
      >
        {/* Identity Protection */}
        <div className="card">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 20,
            }}
          >
            <Shield size={20} style={{ color: 'var(--primary-navy)' }} />
            <h3 style={{ fontSize: 18, fontWeight: 600 }}>
              Identity Protection
            </h3>
          </div>

          <div className="form-group">
            <label className="form-label">Display Name (Alias)</label>
            <input
              type="text"
              className="form-input"
              value={profile.displayName}
              onChange={(e) =>
                setProfile({ ...profile, displayName: e.target.value })
              }
              placeholder="Your interviewer alias"
            />
            <p
              style={{
                fontSize: 12,
                color: 'var(--text-muted)',
                marginTop: 4,
              }}
            >
              This name will be shown to clients instead of your real name
            </p>
          </div>

          <div className="form-group">
            <label className="form-label">Professional Bio</label>
            <textarea
              className="form-input"
              value={profile.bio}
              onChange={(e) =>
                setProfile({ ...profile, bio: e.target.value })
              }
              rows={3}
              placeholder="Brief professional background (no personal details)"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Specializations</label>
            <input
              type="text"
              className="form-input"
              value={profile.specializations}
              onChange={(e) =>
                setProfile({ ...profile, specializations: e.target.value })
              }
              placeholder="e.g., Technical interviews, Product Management, System Design"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Years of Experience</label>
            <input
              type="number"
              className="form-input"
              value={profile.years_experience}
              onChange={(e) =>
                setProfile({ ...profile, years_experience: e.target.value })
              }
              placeholder="e.g., 10"
              style={{ maxWidth: 200 }}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Avatar Style</label>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {AVATAR_STYLES.map(({ key, label }) => {
                const seed = profile.displayName.trim() || profile.avatarSeed
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setProfile({ ...profile, avatarStyle: key })}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 8,
                      padding: 16,
                      border: `2px solid ${profile.avatarStyle === key ? 'var(--primary-navy)' : 'var(--border)'}`,
                      borderRadius: 12,
                      background: profile.avatarStyle === key ? 'rgba(45,55,72,0.04)' : 'white',
                      cursor: 'pointer',
                      minWidth: 80,
                    }}
                  >
                    <img
                      src={avatarUrlForStyle(key, seed)}
                      alt={label}
                      style={{ width: 48, height: 48, borderRadius: '50%', background: '#f0f0f0' }}
                    />
                    <span style={{ fontSize: 12, fontWeight: 500 }}>{label}</span>
                  </button>
                )
              })}
            </div>
          </div>

          {saveMessage && (
            <div
              style={{
                padding: 12,
                borderRadius: 8,
                marginBottom: 16,
                fontSize: 14,
                background: saveMessage.includes('Failed')
                  ? 'rgba(229, 62, 62, 0.08)'
                  : 'rgba(72, 187, 120, 0.08)',
                color: saveMessage.includes('Failed')
                  ? 'var(--danger)'
                  : 'var(--success)',
              }}
            >
              {saveMessage}
            </div>
          )}

          <button
            className="btn btn-primary"
            onClick={handleSaveProfile}
            disabled={saving}
          >
            <Save size={18} />
            {saving ? 'Saving...' : 'Save Profile'}
          </button>
        </div>

        {/* Notifications */}
        <div className="card">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 20,
            }}
          >
            <Bell size={20} style={{ color: 'var(--primary-navy)' }} />
            <h3 style={{ fontSize: 18, fontWeight: 600 }}>Notifications</h3>
          </div>

          {[
            {
              key: 'sessionReminders',
              title: 'Session Reminders',
              desc: 'Get notified before scheduled interviews',
            },
            {
              key: 'evaluationReminders',
              title: 'Evaluation Reminders',
              desc: 'Remind me about pending evaluations',
            },
            {
              key: 'newClients',
              title: 'New Client Assignments',
              desc: 'Notify me when assigned new clients',
            },
          ].map((item) => (
            <div
              key={item.key}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px 0',
                borderBottom: '1px solid var(--border)',
              }}
            >
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>
                  {item.title}
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                  {item.desc}
                </div>
              </div>
              <label
                style={{
                  position: 'relative',
                  width: 48,
                  height: 26,
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={
                    notifications[item.key as keyof typeof notifications]
                  }
                  onChange={(e) =>
                    setNotifications({
                      ...notifications,
                      [item.key]: e.target.checked,
                    })
                  }
                  style={{ opacity: 0, width: 0, height: 0 }}
                />
                <span
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: notifications[
                      item.key as keyof typeof notifications
                    ]
                      ? 'var(--success)'
                      : 'var(--border)',
                    borderRadius: 13,
                    transition: 'background 0.2s',
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      width: 20,
                      height: 20,
                      background: 'white',
                      borderRadius: '50%',
                      top: 3,
                      left: notifications[
                        item.key as keyof typeof notifications
                      ]
                        ? 25
                        : 3,
                      transition: 'left 0.2s',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    }}
                  />
                </span>
              </label>
            </div>
          ))}
        </div>

        {/* Password */}
        <div className="card">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 20,
            }}
          >
            <Lock size={20} style={{ color: 'var(--primary-navy)' }} />
            <h3 style={{ fontSize: 18, fontWeight: 600 }}>Security</h3>
          </div>

          {passwordError && (
            <div
              style={{
                padding: 12,
                borderRadius: 8,
                marginBottom: 16,
                fontSize: 14,
                background: 'rgba(229, 62, 62, 0.08)',
                color: 'var(--danger)',
              }}
            >
              {passwordError}
            </div>
          )}
          {passwordSuccess && (
            <div
              style={{
                padding: 12,
                borderRadius: 8,
                marginBottom: 16,
                fontSize: 14,
                background: 'rgba(72, 187, 120, 0.08)',
                color: 'var(--success)',
              }}
            >
              {passwordSuccess}
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Current Password</label>
            <input
              type="password"
              name="currentPassword"
              className="form-input"
              placeholder="Enter current password"
              value={passwordData.currentPassword}
              onChange={handlePasswordChange}
            />
          </div>
          <div className="form-group">
            <label className="form-label">New Password</label>
            <input
              type="password"
              name="newPassword"
              className="form-input"
              placeholder="Enter new password"
              value={passwordData.newPassword}
              onChange={handlePasswordChange}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Confirm New Password</label>
            <input
              type="password"
              name="confirmPassword"
              className="form-input"
              placeholder="Confirm new password"
              value={passwordData.confirmPassword}
              onChange={handlePasswordChange}
            />
          </div>
          <button
            className="btn btn-primary"
            onClick={handleUpdatePassword}
            disabled={submittingPassword}
          >
            <Lock size={18} />
            {submittingPassword ? 'Updating...' : 'Update Password'}
          </button>
        </div>
      </div>
    </div>
  )
}
